/**
 * Agency credit ledger — PURE core (no AWS SDK, no Node deps).
 *
 * This module is imported by the frontend (Vite source alias), the NestJS API,
 * AND the bundled Lambdas (Trigger, phase-checker, reaper). So it MUST stay
 * dependency-free. The risky money logic lives here as pure functions:
 *   - the credit cost map + estimateAuditCost
 *   - two-bucket consumption split (subscription-first)
 *   - idempotency-key builders
 *   - balanceFromEvents (journal-is-truth reducer)
 *   - buildReserve/Commit/Refund/Grant param builders → return plain
 *     DynamoDB TransactWriteItems input (JSON). Consumers just `.send()` them.
 *
 * Canonical design: docs/billing/metering-and-entitlements.md §7-§8.
 */

import type { AuditType } from './audit';

// ───────────────────────── cost map ─────────────────────────

/**
 * Credit cost of each atomic billable primitive. Single source of truth.
 * Operator-overridable per-tenant later (creditCostOverride) — Phase 6.
 */
export const CREDIT_COST = {
  engine_call: 1, // 1 prompt × 1 engine
  page_analyzed: 1, // 1 page through the website pipeline
  fetch: 1, // 1 earned-media fetch+judge
  news_query: 1, // 1 news monitoring query
  competitor: 2, // 1 tracked competitor, charged once per BP-axis run (narrative/authority/referral)
  report_export: 15, // 1 generated report (storage-backed)
  product_review: 1, // 1 catalogue item (product OR category page) GEO-optimized
} as const;

export type Primitive = keyof typeof CREDIT_COST;

/**
 * A Website Audit fans out to N Phase-1 agents that each analyze the SAME page
 * set at 1 credit/page (`page_analyzed`). Agency — the only credit-metered tier
 * — runs all three (content_quality + content_freshness + structural_seo), so a
 * website audit costs **3 credits per page**. The reserve multiplies pages by
 * this; the commit sums `pages_analyzed` across the three agent summaries (=
 * 3×actual when all run). Basic runs a single agent, but basic isn't
 * credit-metered, so 3 is the only value the credit path ever sees. Bump this
 * if the Phase-1 fan-out changes.
 */
export const WEBSITE_AUDIT_AGENTS = 3;

/**
 * Credit cost per distribution channel of one News Watch (Studio de contenu)
 * run — the billable unit is the CHANNEL (each channel = the article and/or its
 * platform variants). A run generating 3 channels costs `3 × 2 = 6` credits.
 * Charge is exact at reserve time (channel count is known up front → commit ==
 * reserved, no true-up). Attributed to the `news_query` primitive in the
 * per-brand usage rollup. Non-agency tiers aren't credit-metered — they're
 * bounded by cadence + the run-now gate (news_watch is Elite/Enterprise/Agency).
 */
export const NEWS_WATCH_CHANNEL_COST = 2;
/** Default channel count when a run carries no explicit config (cron path uses
 *  the orchestrator's default: blog_seo + linkedin_post + x_thread). */
export const NEWS_WATCH_DEFAULT_CHANNELS = 3;

export interface CostEstimate {
  total: number;
  breakdown: Partial<Record<Primitive, number>>;
  /**
   * Whether this estimate is exact (deterministic axes: we know prompts×engines
   * up front) or a max to be trued-up at commit (website pages, earned-media
   * fetch). Drives whether the commit step reads `actual` from the summary.
   */
  exact: boolean;
}

/**
 * Estimate the credit cost of an audit at RESERVE time.
 *
 * Deterministic axes (brand_*, news_watch) → exact (prompts × engines known in
 * advance). website → max pages × page cost; earned_media → max fetch × fetch
 * cost — both `exact: false`, trued-up at commit from the agent summary.
 *
 * All three brand-perception axes additionally charge for tracked competitors:
 * the competitor matrix is probed on EVERY BP run (narrative, authority, AND
 * referral — competitor positioning is measured on each axis), so each tracked
 * competitor costs `CREDIT_COST.competitor` once per run per axis (NOT per
 * engine/prompt). `competitorCount` = brand.competitors.length (the set actually
 * probed; there is no paused state). Still exact: count is known at reserve time.
 *
 * The competitor charge is CONDITIONAL on there being engine work: the matrix is
 * derived from the engine responses themselves (fanout_worker passes
 * `competitors` into `_build_row`), so zero prompts ⇒ zero responses ⇒ no matrix
 * and nothing to bill. A BP axis with `prompts × engines == 0` therefore costs
 * NOTHING at all. Without this the competitor line was charged for a run that
 * produced no output whatsoever: Ecomiz had 8 competitors and 0 active prompts,
 * so all three daily axes billed 8 × 2 = 16 credits each for an audit whose
 * summary was `query_count: 0, completed: 0` — 48 credits/day burning a 500-credit
 * balance to 0 in ~10 days (prod, 2026-08-04 → 2026-08-14). The Trigger Lambda's
 * step-5b prompt gate now blocks that run outright; this is the money-side
 * backstop so no reserve can be taken for it even if the gates disagree.
 */
export function estimateAuditCost(
  auditType: AuditType,
  cfg: {
    promptCount?: number;
    engineCount?: number;
    maxPages?: number; // pages_per_audit (website)
    maxFetch?: number; // fetch budget (earned_media)
    newsQueryCount?: number;
    competitorCount?: number; // any brand_* axis — tracked competitors
    channelCount?: number; // news_watch — number of distribution channels
  },
): CostEstimate {
  switch (auditType) {
    case 'brand_narrative':
    case 'brand_authority':
    case 'brand_referral': {
      const calls = (cfg.promptCount ?? 0) * (cfg.engineCount ?? 0);
      // No engine work ⇒ no responses ⇒ no competitor matrix ⇒ charge nothing.
      if (calls <= 0) return { total: 0, breakdown: { engine_call: 0 }, exact: true };
      const competitors = cfg.competitorCount ?? 0;
      const engineCredits = calls * CREDIT_COST.engine_call;
      const competitorCredits = competitors * CREDIT_COST.competitor;
      const breakdown: Partial<Record<Primitive, number>> = { engine_call: calls };
      if (competitors > 0) breakdown.competitor = competitors;
      return { total: engineCredits + competitorCredits, breakdown, exact: true };
    }
    case 'news_watch': {
      // Per-channel charge: NEWS_WATCH_CHANNEL_COST per distribution channel the
      // user selected (each channel = the article and/or its variants). Exact —
      // channel count is known at reserve time. Attributed to `news_query` so
      // the breakdown total equals the charge.
      const channels = cfg.channelCount ?? NEWS_WATCH_DEFAULT_CHANNELS;
      const total = channels * NEWS_WATCH_CHANNEL_COST;
      return {
        total,
        breakdown: { news_query: total },
        exact: true,
      };
    }
    case 'website': {
      // 3 Phase-1 agents each analyze every page → 3 credits/page (see
      // WEBSITE_AUDIT_AGENTS). `breakdown.page_analyzed` carries the BILLED unit
      // count (pages × agents) so the per-type rollup matches the charge.
      const pages = cfg.maxPages ?? 0;
      const billed = pages * WEBSITE_AUDIT_AGENTS;
      return {
        total: billed * CREDIT_COST.page_analyzed,
        breakdown: { page_analyzed: billed },
        exact: false,
      };
    }
    case 'earned_media': {
      const f = cfg.maxFetch ?? 0;
      return { total: f * CREDIT_COST.fetch, breakdown: { fetch: f }, exact: false };
    }
    // legacy combined 'brand' + 'social' aren't credit-dispatched.
    default:
      return { total: 0, breakdown: {}, exact: true };
  }
}

/** Cost of one report export (separate action, not an audit). */
export function estimateReportExport(): CostEstimate {
  return { total: CREDIT_COST.report_export, breakdown: { report_export: 1 }, exact: true };
}

// ─────────────── Product module (retailer) — not AuditTypes ───────────────
//
// The Product module's two paid runs are NOT `AuditType`s: they never route
// through the Trigger Lambda / phase-checker, so `estimateAuditCost` can't see
// them. They reserve from the API (RetailerCreditService) and settle at the
// existing read-time finalize hooks. Costs mirror the axes they resemble:
//   optimize    → 1 credit per catalogue ITEM reviewed (product OR category
//                 page): one fetch + one Nemotron rewrite + one embed each.
//                 NOT exact — WAF-blocked / 404 items are refunded at settle.
//   measurement → 1 credit per (prompt × engine), exactly like a BP axis. Exact
//                 at reserve time (the work-list is fully planned up front), but
//                 still trued-up: rows that never land are refunded.

/** Optimize run: `itemCount` items reviewed at 1 credit each. */
export function estimateProductOptimizeCost(itemCount: number): CostEstimate {
  const n = Math.max(0, itemCount);
  return {
    total: n * CREDIT_COST.product_review,
    breakdown: { product_review: n },
    exact: false,
  };
}

/** Measurement run: `callCount` = Σ (active prompts × segment engines). */
export function estimateProductMeasureCost(callCount: number): CostEstimate {
  const n = Math.max(0, callCount);
  return {
    total: n * CREDIT_COST.engine_call,
    breakdown: { engine_call: n },
    exact: true,
  };
}

// ───────────────────────── motifs (ledger labels) ─────────────────────────

/** Competitor-matrix line motif on a brand_referral run (separate ledger line). */
export const MOTIF_COMPETITOR_LINE = 'Analyse concurrents';
/** Synchronous single competitor analysis (Concurrents page). */
export const MOTIF_COMPETITOR_ANALYSIS = 'Analyse concurrent';
/** Report generation (report_export charge). */
export const MOTIF_REPORT_EXPORT = 'Génération de rapport';
/** Product-module optimize run (1 credit / catalogue item reviewed). */
export const MOTIF_PRODUCT_OPTIMIZE = 'Optimisation produits';
/** Product-module measurement run (1 credit / prompt × modèle, like a BP axis). */
export const MOTIF_PRODUCT_MEASURE = 'Mesure GEO produits';

/**
 * Human-readable French motif for an audit's main (engine/page/fetch) line.
 * ALWAYS returns a non-empty string so every ledger row has a motif.
 */
export function auditMotif(auditType: AuditType | string): string {
  switch (auditType) {
    case 'website':
      return 'Audit Website';
    case 'earned_media':
      return 'Audit Médias acquis';
    case 'brand_narrative':
      return 'Audit Image de marque';
    case 'brand_authority':
      return 'Audit Autorité';
    case 'brand_referral':
      return 'Audit Recommandations';
    case 'news_watch':
      return 'Veille news';
    case 'brand':
      return 'Audit Image de marque';
    default:
      return 'Audit';
  }
}

// ───────────────────────── agency subscription plans ─────────────────────────

/**
 * Agency recurring subscription plans (catalog). `key` = the Stripe lookup_key
 * (setup-stripe-catalog.ts) AND the id passed to /billing/checkout/agency.
 * `monthlyAllocation` is the credits granted each invoice (also the value
 * persisted to settings.agencyCredits on conversion).
 */
export const AGENCY_PLANS = [
  // Agency XS went PUBLIC on 2026-09-08 (was adminOnly since 2026-07-16): it is the
  // entry card on the marketing pricing page and self-serve-selectable in-app. It is
  // the one plan carrying a brand cap, which an admin still poses by hand (see below).
  { key: 'agency_xs_monthly', label: 'Agency XS', monthlyAllocation: 4000, priceEur: 240 },
  // Admin-only: provisioned from the admin tenant-create page, never shown in-app
  // (BillingSettings filters `adminOnly`) or on the marketing site. Not self-serve-
  // selectable. Backend lookups (agencyPlan / agencyPlanByAllocation) still resolve it.
  { key: 'agency_demarrage_monthly', label: 'Agency Démarrage', monthlyAllocation: 1660, priceEur: 100, adminOnly: true },
  { key: 'agency_s_monthly', label: 'Agency S', monthlyAllocation: 8000, priceEur: 480 },
  { key: 'agency_m_monthly', label: 'Agency M', monthlyAllocation: 19000, priceEur: 950 },
  { key: 'agency_l_monthly', label: 'Agency L', monthlyAllocation: 110000, priceEur: 4980 },
] as const;

/**
 * Agency XS is the ONE agency plan with a hard brand cap — every other agency
 * plan is unlimited. The cap is carried as `tenant.settings.quotaOverrides.brands`
 * (resolveEntitlement applies an override BEFORE the unlimited check, so it bites
 * on a tier whose default is -1). Nothing poses the cap automatically: an admin types
 * the number on the tenant, which means a self-serve XS subscriber runs uncapped until
 * one does (deliberate, 2026-09-09). Leaving XS DOES clear it automatically — see
 * withoutXsBrandCap in webhooks.controller.ts, keyed on the OLD allocation being XS so
 * a deliberate override on a non-XS agency tenant survives. Without that clear, an
 * upgrade to S/M/L would keep the tenant blocked at 15 brands on an unlimited plan.
 */
export const AGENCY_XS_MAX_BRANDS = 15;

/** True if `alloc` is Agency XS's monthly allocation (the brand-capped plan). */
export function isAgencyXsAllocation(alloc: number): boolean {
  return alloc === (agencyPlan('agency_xs_monthly')?.monthlyAllocation ?? -1);
}

export type AgencyPlanKey = (typeof AGENCY_PLANS)[number]['key'];

export function agencyPlan(key: string): { key: AgencyPlanKey; label: string; monthlyAllocation: number; priceEur: number; adminOnly?: boolean } | undefined {
  return AGENCY_PLANS.find((p) => p.key === key);
}

/** Look up an agency plan by its monthlyAllocation (reverse of agencyPlan). */
export function agencyPlanByAllocation(alloc: number) {
  return AGENCY_PLANS.find((p) => p.monthlyAllocation === alloc);
}

/**
 * Prorate a monthly allocation to the fraction of the cycle still remaining.
 * The denominator is a 30-DAY basis (monthlyAllocation is a per-month figure),
 * NOT the raw period length — otherwise a short calendar-aligned first stub
 * (e.g. 5 days) prorates to ≈ the FULL month (real bug: an L upgrade on a 5-day
 * stub granted 89k instead of ~18k). `nowMs`/`periodEndMs` are absolute epoch ms.
 * Returns a non-negative integer.
 */
export function prorateAllocation(monthlyAllocation: number, nowMs: number, periodEndMs: number): number {
  const PRORATION_BASIS_MS = 30 * 86_400_000;
  const remainingMs = Math.max(0, periodEndMs - nowMs);
  const fraction = Math.min(1, remainingMs / PRORATION_BASIS_MS);
  return Math.max(0, Math.round(monthlyAllocation * fraction));
}

/**
 * Toggle-safe upgrade grant. Tracks a per-period high-water mark of prorated
 * credits ALREADY granted this cycle; an upgrade grants only the shortfall up to
 * the new plan's prorated target. Correct under any up/down/up sequence:
 *   S→M→S→M→L grants M's delta, then nothing (already ≥ M), then L−M → total = L prorated.
 * `alreadyGrantedThisPeriod` resets to 0 by the caller when the billing period rolls.
 */
export function upgradeGrantAmount(
  newMonthlyAllocation: number,
  alreadyGrantedThisPeriod: number,
  nowMs: number,
  periodEndMs: number,
): number {
  const target = prorateAllocation(newMonthlyAllocation, nowMs, periodEndMs);
  return Math.max(0, target - Math.max(0, alreadyGrantedThisPeriod));
}

// ───────────────────────── top-up packs (one-shot) ─────────────────────────

/**
 * Agency one-shot credit packs. `key` = the Stripe catalog lookup_key
 * (setup-stripe-catalog.ts) AND the id the frontend/backend pass around.
 * Prices here are display-only; Stripe is the source of truth for the charge.
 */
export const TOPUP_PACKS = [
  { key: 'agency_topup_1k', credits: 1000, priceEur: 70 },
  { key: 'agency_topup_5k', credits: 5000, priceEur: 290 },
  { key: 'agency_topup_25k', credits: 25000, priceEur: 1150 },
] as const;

export type TopupPackKey = (typeof TOPUP_PACKS)[number]['key'];

export function topupPack(key: string): { key: TopupPackKey; credits: number; priceEur: number } | undefined {
  return TOPUP_PACKS.find((p) => p.key === key);
}

// ───────────────────────── balance shape ─────────────────────────

/** Two-bucket credit balance, stored on the usage-counters CREDIT#balance row. */
export interface CreditBalance {
  subscriptionCredits: number;
  topupCredits: number;
  reserved: number;
  subscriptionExpireAt?: string; // ISO; informational (replenish is webhook-driven)
  monthlyAllocation?: number;
  pricePerCreditCents?: number;
  /**
   * Monotonic lifetime total of credits the tenant PAID for — every
   * `source: 'stripe'` grant (monthly subscription replenishment + mid-cycle
   * upgrade proration + one-off top-up packs). Deliberately EXCLUDES admin
   * adjustments and the agency signup trial, so it reads as "credits bought".
   * Never decremented; consumption does not touch it (unlike the three buckets
   * above, which are a live balance).
   *
   * Added 2026-07-29 for the HubSpot CRM sync (`credits_purchased_total`).
   * Absent on tenants whose grants all predate it — backfill with
   * scripts/backfill-lifetime-purchased.ts.
   */
  lifetimePurchased?: number;
}

export const ZERO_BALANCE: CreditBalance = {
  subscriptionCredits: 0,
  topupCredits: 0,
  reserved: 0,
};

/** available = sub + topup − reserved (never negative). */
export function availableCredits(b: CreditBalance): number {
  return Math.max(0, b.subscriptionCredits + b.topupCredits - b.reserved);
}

/**
 * Split an `amount` to consume across buckets, subscription-first (doc §8.1).
 * Caller guarantees amount ≤ sub+topup (the reserve already held it).
 */
export function splitConsumption(
  amount: number,
  b: Pick<CreditBalance, 'subscriptionCredits' | 'topupCredits'>,
): { fromSub: number; fromTopup: number } {
  const fromSub = Math.min(amount, b.subscriptionCredits);
  return { fromSub, fromTopup: amount - fromSub };
}

// ───────────────────────── ledger events ─────────────────────────

export type LedgerEventType =
  | 'reserve'
  | 'commit'
  | 'refund'
  | 'grant' // packs + replenish
  | 'expire'
  | 'adjust';

export interface LedgerEvent {
  eventType: LedgerEventType;
  /** Signed delta to AVAILABLE credits for this row's CURRENT state (negative =
   *  consumed/held). A `reserve` row carries `-held` because a hold lowers
   *  available; when the row is settled in place it is REWRITTEN to `-actual`
   *  (commit) or `0` (refund). */
  amount: number;
  subDelta: number;
  topupDelta: number;
  reservedDelta: number;
  /** Human-readable French label, ALWAYS set (e.g. "Audit Recommandations",
   *  "Analyse concurrents", "Génération de rapport"). Drives the ledger UI. */
  motif?: string;
  /** Available-balance snapshot AFTER this row's effect (audit + drift detect). */
  balanceAfter?: number;
}

/**
 * A single billable line of one reservation. Non-BP audits have ONE line
 * ('audit'); a brand_* run (narrative/authority/referral) has TWO from the
 * start — the engine calls ('audit') and the competitor matrix probe
 * ('competitor', 2 cr each) — so the ledger shows "Analyse concurrents" as its
 * own line.
 */
export type CreditLineKey = 'audit' | 'competitor';

/**
 * Recompute available balance from a sequence of events (journal-is-truth).
 * The stored counter is a cache of this; reconciliation compares the two.
 */
export function balanceFromEvents(
  events: Array<Pick<LedgerEvent, 'subDelta' | 'topupDelta' | 'reservedDelta'>>,
): { subscriptionCredits: number; topupCredits: number; reserved: number } {
  let subscriptionCredits = 0;
  let topupCredits = 0;
  let reserved = 0;
  for (const e of events) {
    subscriptionCredits += e.subDelta;
    topupCredits += e.topupDelta;
    reserved += e.reservedDelta;
  }
  return { subscriptionCredits, topupCredits, reserved };
}

// ───────────────────────── idempotency keys ─────────────────────────

/**
 * Reserve idempotency key. For cron runs this MUST be the schedule fire
 * (scheduleName:triggeredFor), NOT the auditId — the Trigger Lambda mints a
 * fresh auditId per cron fire, so a redelivered SQS message would otherwise
 * double-reserve (doc §7.2, §12.2).
 */
export function reserveIdemKey(input: {
  source: 'manual' | 'cron';
  auditId: string;
  scheduleName?: string;
  triggeredFor?: string;
}): string {
  if (input.source === 'cron' && input.scheduleName && input.triggeredFor) {
    return `sched:${input.scheduleName}:${input.triggeredFor}`;
  }
  return `audit:${input.auditId}`;
}

export function reportReserveIdemKey(reportId: string): string {
  return `report:${reportId}`;
}

/**
 * Product-module reserve idempotency key. The API mints the runId itself and
 * reserves BEFORE seeding the run row, so the runId is already unique per user
 * action — a double-click creates two runs, two reservations, and (correctly)
 * two charges. The anchor still exists so a retried reserve for the SAME runId
 * (network retry inside the request) can never double-hold.
 */
export function retailerReserveIdemKey(kind: 'optimize' | 'measure', runId: string): string {
  return `retailer:${kind}:${runId}`;
}

// ───────────────────────── transaction plans ─────────────────────────
//
// Pure builders returning neutral, document-client-shaped (plain JS values, no
// marshalling) pieces. Each consumer (NestJS DocumentClient, bundled Lambdas)
// wraps these in a TransactWriteCommand and `.send()`s. This keeps the atomic
// money logic — conditions, expressions, deltas — in ONE tested place while the
// network call stays per-consumer. Caller supplies the resolved table names.

/** Conditional UpdateItem on the CREDIT#balance row. */
export interface CounterUpdatePlan {
  TableName: string;
  Key: { PK: string; SK: string };
  UpdateExpression: string;
  ConditionExpression?: string;
  ExpressionAttributeNames?: Record<string, string>;
  ExpressionAttributeValues: Record<string, unknown>;
}

/** A Put of one append-only journal row. */
export interface LedgerPutPlan {
  TableName: string;
  Item: Record<string, unknown>;
  ConditionExpression?: string;
  ExpressionAttributeNames?: Record<string, string>;
  ExpressionAttributeValues?: Record<string, unknown>;
}

/** An in-place Update of an existing journal row (lifecycle settle). The row is
 *  addressed by its reservation-time SK so the ledger LINE stays put and merely
 *  transforms state (reserve → commit/refund) — see docs §7.6. */
export interface LedgerUpdatePlan {
  TableName: string;
  Key: { PK: string; SK: string };
  UpdateExpression: string;
  ExpressionAttributeNames?: Record<string, string>;
  ExpressionAttributeValues: Record<string, unknown>;
}

/** A full reserve/commit/refund/grant = a counter update + a journal put, run
 *  together in ONE TransactWriteItems so the cache can never diverge. */
export interface LedgerTxPlan {
  counter: CounterUpdatePlan;
  event: LedgerPutPlan;
}

const CREDIT_TTL_DAYS = 730; // 24-month journal retention

export function creditBalanceKey(tenantId: string): { PK: string; SK: string } {
  return { PK: `TENANT#${tenantId}`, SK: 'CREDIT#balance' };
}

// ───────────────────────── per-brand monthly usage rollup ─────────────────────────
//
// Pre-aggregated spend, written at COMMIT/CHARGE time so the "Utilisation des
// crédits" view (donut par marque + by-type) reads O(brands) tiny rows instead
// of scanning the event journal (docs §11). One row per (tenant × month × brand):
//   PK = TENANT#<id>  SK = USAGE#<YYYY-MM>#BRAND#<brandId>
//   credits          : running total consumed this month for the brand (number)
//   type_<primitive> : same total split by primitive (flat TOP-LEVEL attrs —
//                      ADD only auto-creates top-level keys, never a nested map
//                      path, which would throw ValidationException on first write)
//   brandId, month, updatedAt, ttl
// The ADD is folded into the same TransactWriteItems as the settle/charge, so it
// inherits that transaction's idempotency (RESV open→terminal flip / charge
// conditional) and can never drift from the balance.

const USAGE_ROLLUP_TTL_DAYS = 400; // ~13 months — covers a full year of monthly views

/** SK for one (month × brand) rollup row. `month` = 'YYYY-MM'. */
export function usageRollupSk(month: string, brandId: string): string {
  return `USAGE#${month}#BRAND#${brandId}`;
}

/** SK prefix to Query a whole month's per-brand rollups (begins_with). */
export function usageRollupMonthPrefix(month: string): string {
  return `USAGE#${month}#BRAND#`;
}

/** 'YYYY-MM' for a ms timestamp, UTC (matches the calendar-month billing view). */
export function usageMonthOf(nowMs: number): string {
  const d = new Date(nowMs);
  const m = (d.getUTCMonth() + 1).toString().padStart(2, '0');
  return `${d.getUTCFullYear()}-${m}`;
}

/**
 * Build the atomic rollup Update for one consumption of `amount` credits by
 * `brandId`, attributed to `primitive`. Folded into the caller's existing
 * commit/charge TransactWriteItems. No-op intent if amount<=0 or brandId absent
 * (caller should skip the Update entirely in that case). `removeUndefinedValues`
 * is NOT needed — every value is concrete.
 */
export function buildUsageRollupUpdate(input: {
  countersTable: string;
  tenantId: string;
  brandId: string;
  primitive: Primitive;
  amount: number;
  nowMs: number;
}): CounterUpdatePlan {
  const month = usageMonthOf(input.nowMs);
  const ttl = Math.floor(input.nowMs / 1000) + USAGE_ROLLUP_TTL_DAYS * 86400;
  return {
    TableName: input.countersTable,
    Key: { PK: `TENANT#${input.tenantId}`, SK: usageRollupSk(month, input.brandId) },
    // ADD on TOP-LEVEL attributes only (auto-creates them on first write). The
    // per-primitive split is a flat `type_<primitive>` key, NOT a nested map
    // path — ADD into a nested path throws ValidationException when the parent
    // map is absent. SET-if-not-exists stamps the descriptive attrs once.
    UpdateExpression:
      'ADD credits :amt, #prim :amt ' +
      'SET brandId = if_not_exists(brandId, :b), #m = if_not_exists(#m, :month), ' +
      'updatedAt = :now, #ttl = if_not_exists(#ttl, :ttl)',
    ExpressionAttributeNames: { '#prim': usageTypeAttr(input.primitive), '#m': 'month', '#ttl': 'ttl' },
    ExpressionAttributeValues: {
      ':amt': input.amount,
      ':b': input.brandId,
      ':month': month,
      ':now': new Date(input.nowMs).toISOString(),
      ':ttl': ttl,
    },
  };
}

/** Flat per-primitive attribute name on a rollup row (e.g. 'type_engine_call'). */
export function usageTypeAttr(primitive: Primitive): string {
  return `type_${primitive}`;
}

/** One per-brand monthly usage row, as read back by GET /credits/usage. */
export interface BrandUsageRollup {
  brandId: string;
  month: string;
  credits: number;
  byType: Partial<Record<Primitive, number>>;
}

/**
 * Reverse-ts SK so the tenant journal reads newest-first without a GSI.
 * The settle path REUSES the reservation-time SK so a line stays in place and
 * transforms (reserve → commit/refund) instead of jumping to the top.
 */
export function eventSk(nowMs: number, uuid: string): string {
  const reverse = (1e15 - nowMs).toString().padStart(16, '0');
  return `EVT#${reverse}#${uuid}`;
}

interface LedgerMeta {
  resourceId?: string;
  brandId?: string;
  reservationId?: string;
  reason?: string;
  motif?: string;
  balanceAfter?: number;
  source: 'manual' | 'cron' | 'stripe' | 'admin' | 'reaper' | 'system';
  metadata?: Record<string, unknown>;
}

function buildEventItem(input: {
  countersTable: string; // unused; events go to eventsTable
  eventsTable: string;
  tenantId: string;
  eventType: LedgerEventType;
  amount: number;
  subDelta: number;
  topupDelta: number;
  reservedDelta: number;
  nowIso: string;
  nowMs: number;
  uuid: string;
  meta: LedgerMeta;
  /** Reuse a pre-computed SK (multi-line reserve gives each line its own). */
  sk?: string;
  lineKey?: CreditLineKey;
}): LedgerPutPlan {
  const ttl = Math.floor(input.nowMs / 1000) + CREDIT_TTL_DAYS * 86400;
  return {
    TableName: input.eventsTable,
    Item: {
      PK: `TENANT#${input.tenantId}`,
      SK: input.sk ?? eventSk(input.nowMs, input.uuid),
      ...(input.lineKey ? { lineKey: input.lineKey } : {}),
      ...(input.meta.resourceId ? { resourcePK: `RES#${input.meta.resourceId}` } : {}),
      tenantId: input.tenantId,
      eventId: input.uuid,
      timestamp: input.nowIso,
      eventType: input.eventType,
      amount: input.amount,
      subDelta: input.subDelta,
      topupDelta: input.topupDelta,
      reservedDelta: input.reservedDelta,
      ...(input.meta.resourceId ? { resourceId: input.meta.resourceId } : {}),
      ...(input.meta.brandId ? { brandId: input.meta.brandId } : {}),
      ...(input.meta.reservationId ? { reservationId: input.meta.reservationId } : {}),
      ...(input.meta.reason ? { reason: input.meta.reason } : {}),
      ...(input.meta.motif ? { motif: input.meta.motif } : {}),
      ...(typeof input.meta.balanceAfter === 'number' ? { balanceAfter: input.meta.balanceAfter } : {}),
      source: input.meta.source,
      ...(input.meta.metadata ? { metadata: input.meta.metadata } : {}),
      ttl,
    },
  };
}

// ─── lifecycle-collapsed ledger: ONE row per line, reserved→settled in place ───
//
// A reservation has one or more LINES (an audit = 1 line; a brand_referral run =
// 2 lines: "Audit Recommandations" + "Analyse concurrents"). Each line is ONE
// journal row written at reserve time and UPDATED IN PLACE at settle — so the
// ledger shows a single line per line-of-work that transforms Réservation →
// Consommation/Annulé without ever jumping in the timeline (it keeps its
// reserve-time SK). This is a deliberate, documented deviation from strict
// append-only (docs §7.6): the in-place update rides inside the SAME
// TransactWriteItems as the RESV open→terminal flip, which already provides
// idempotency against stream redelivery. `balanceFromEvents` stays correct at
// every instant because each row's CURRENT deltas net out (reserve: reservedDelta
// only; commit: sub/topup only; refund: all zero).

/** One billable line of a reservation, as supplied at reserve time. */
export interface ReserveLine {
  lineKey: CreditLineKey;
  amount: number; // credits held for this line
  motif: string; // French ledger label, always set
}

/** Per-line SK so the settle path can address each row in place. */
export function lineEventSk(nowMs: number, baseUuid: string, index: number): string {
  return eventSk(nowMs, `${baseUuid}-${index}`);
}

/**
 * RESERVE (multi-line): move `Σ lines.amount` into `reserved` (ONE conditional
 * counter update) + write ONE journal row PER LINE (held state: amount=-held,
 * reservedDelta=+held). The optimistic lock on the exact read bucket values is
 * unchanged from the single-line version. Returns the per-line SKs so the caller
 * can persist them on the reservation record for the in-place settle.
 */
export function buildReserveLinesPlan(input: {
  countersTable: string;
  eventsTable: string;
  tenantId: string;
  reservationId: string;
  lines: ReserveLine[];
  /** Optimistic-lock bucket values the caller READ (see single-line note above). */
  expected: { subscriptionCredits: number; topupCredits: number; reserved: number };
  /** available (= sub+topup−reserved) BEFORE this reserve, for balanceAfter. */
  availableBefore: number;
  nowIso: string;
  nowMs: number;
  baseUuid: string;
  meta: Omit<LedgerMeta, 'source' | 'reservationId' | 'motif' | 'balanceAfter'> & {
    source: 'manual' | 'cron';
  };
}): { counter: CounterUpdatePlan; events: Array<LedgerPutPlan & { lineKey: CreditLineKey; sk: string }> } {
  const total = input.lines.reduce((s, l) => s + l.amount, 0);
  let running = input.availableBefore;
  const events = input.lines.map((line, i) => {
    running -= line.amount; // a hold lowers available
    const sk = lineEventSk(input.nowMs, input.baseUuid, i);
    const put = buildEventItem({
      countersTable: input.countersTable,
      eventsTable: input.eventsTable,
      tenantId: input.tenantId,
      eventType: 'reserve',
      amount: -line.amount,
      subDelta: 0,
      topupDelta: 0,
      reservedDelta: line.amount,
      nowIso: input.nowIso,
      nowMs: input.nowMs,
      uuid: `${input.baseUuid}-${i}`,
      sk,
      lineKey: line.lineKey,
      meta: { ...input.meta, reservationId: input.reservationId, motif: line.motif, balanceAfter: running },
    });
    return { ...put, lineKey: line.lineKey, sk };
  });
  return {
    counter: {
      TableName: input.countersTable,
      Key: creditBalanceKey(input.tenantId),
      UpdateExpression: 'SET reserved = if_not_exists(reserved, :z) + :amt',
      // Optimistic lock: proceed only if the three buckets are still exactly what
      // the caller read. DynamoDB ConditionExpression forbids BOTH arithmetic
      // (+/-) AND if_not_exists() — only comparisons + attribute_exists/_not_exists.
      ConditionExpression:
        '(attribute_not_exists(subscriptionCredits) OR subscriptionCredits = :esub) AND ' +
        '(attribute_not_exists(topupCredits) OR topupCredits = :etopup) AND ' +
        '(attribute_not_exists(reserved) OR reserved = :eres)',
      ExpressionAttributeValues: {
        ':amt': total,
        ':z': 0,
        ':esub': input.expected.subscriptionCredits,
        ':etopup': input.expected.topupCredits,
        ':eres': input.expected.reserved,
      },
    },
    events,
  };
}

/** One line of a reservation as it settles (commit or refund). */
export interface SettleLine {
  /** Reserve-time SK of this line's journal row. Absent for pre-migration
   *  reservations with no per-line rows — the counter still settles but no row
   *  is updated in place. */
  sk?: string;
  reservedAmount: number; // what this line held
  actual: number; // consumed (≤ reservedAmount); ignored for refund
  motif: string;
}

/**
 * SETTLE (commit OR refund), in place. ONE counter update for the whole
 * reservation + ONE in-place UPDATE of each line's existing journal row (keeps
 * its reserve-time SK + timestamp + motif; rewrites eventType/amount/deltas/
 * balanceAfter). Commit consumes `Σ actual` subscription-first and releases the
 * slack; refund releases the full hold and consumes nothing (net 0 line, shown
 * as "Annulé"). The global sub/topup split is allocated across lines in order so
 * each row's own deltas sum to the truth (`balanceFromEvents` stays correct).
 */
export function buildSettlePlan(input: {
  countersTable: string;
  eventsTable: string;
  tenantId: string;
  outcome: 'commit' | 'refund';
  lines: SettleLine[];
  currentSub: number; // current subscriptionCredits (commit split)
  /** available BEFORE settle (holds still in place), for balanceAfter. */
  availableBefore: number;
  reason?: string; // refund only
  nowIso: string;
  nowMs: number;
  source: 'manual' | 'cron' | 'reaper' | 'system';
}): { counter: CounterUpdatePlan; events: LedgerUpdatePlan[] } {
  const totalReserved = input.lines.reduce((s, l) => s + l.reservedAmount, 0);
  const totalActual = input.outcome === 'commit' ? input.lines.reduce((s, l) => s + l.actual, 0) : 0;
  const { fromSub, fromTopup } = splitConsumption(totalActual, {
    subscriptionCredits: input.currentSub,
    topupCredits: Number.MAX_SAFE_INTEGER,
  });

  let counter: CounterUpdatePlan;
  if (input.outcome === 'commit') {
    counter = {
      TableName: input.countersTable,
      Key: creditBalanceKey(input.tenantId),
      UpdateExpression:
        'SET subscriptionCredits = subscriptionCredits - :fromSub, ' +
        'topupCredits = topupCredits - :fromTopup, ' +
        'reserved = reserved - :reserved',
      ConditionExpression:
        'reserved >= :reserved AND subscriptionCredits >= :fromSub AND topupCredits >= :fromTopup',
      ExpressionAttributeValues: { ':fromSub': fromSub, ':fromTopup': fromTopup, ':reserved': totalReserved },
    };
  } else {
    counter = {
      TableName: input.countersTable,
      Key: creditBalanceKey(input.tenantId),
      UpdateExpression: 'SET reserved = reserved - :reserved',
      ConditionExpression: 'reserved >= :reserved',
      ExpressionAttributeValues: { ':reserved': totalReserved },
    };
  }

  // Allocate the global sub-first split across lines in order so per-row deltas
  // sum to the global split, and compute a running balanceAfter for the column.
  let remainingFromSub = fromSub;
  let running = input.availableBefore;
  const events = input.lines.flatMap((line): LedgerUpdatePlan[] => {
    const lineActual = input.outcome === 'commit' ? line.actual : 0;
    const lineFromSub = Math.min(lineActual, remainingFromSub);
    const lineFromTopup = lineActual - lineFromSub;
    remainingFromSub -= lineFromSub;
    // Settling raises available by the released slack (reserved − actual).
    running += line.reservedAmount - lineActual;

    // Pre-migration line with no journal row → counter-only settle, no update.
    if (!line.sk) return [];

    const names: Record<string, string> = { '#et': 'eventType' };
    const values: Record<string, unknown> = {
      ':et': input.outcome === 'commit' ? 'commit' : 'refund',
      ':amt': -lineActual || 0, // avoid -0 in the journal
      ':sd': -lineFromSub || 0,
      ':td': -lineFromTopup || 0,
      ':rd': 0,
      ':ba': running,
      ':now': input.nowIso,
      ':src': input.source,
    };
    let expr =
      'SET #et = :et, amount = :amt, subDelta = :sd, topupDelta = :td, ' +
      'reservedDelta = :rd, balanceAfter = :ba, settledAt = :now, #s = :src';
    names['#s'] = 'source';
    if (input.outcome === 'refund' && input.reason) {
      expr += ', reason = :rsn';
      values[':rsn'] = input.reason;
    }
    return [
      {
        TableName: input.eventsTable,
        Key: { PK: `TENANT#${input.tenantId}`, SK: line.sk },
        UpdateExpression: expr,
        ExpressionAttributeNames: names,
        ExpressionAttributeValues: values,
      },
    ];
  });

  return { counter, events };
}

/**
 * CHARGE: a direct synchronous debit — consume `amount` sub-first, NO reserved
 * bucket involved (unlike reserve→commit). For in-request actions that either
 * succeed or throw with no async gap (competitor analysis, report generation):
 * the caller checks balance first, does the work, then charges on success.
 * Conditional guards against a stale/raced balance.
 */
export function buildChargePlan(input: {
  countersTable: string;
  eventsTable: string;
  tenantId: string;
  amount: number;
  currentSub: number; // for the sub-first split
  nowIso: string;
  nowMs: number;
  uuid: string;
  primitive: Primitive;
  /** French ledger label, always set (e.g. "Génération de rapport"). */
  motif: string;
  /** available BEFORE this charge, for the balanceAfter snapshot. */
  availableBefore: number;
  /** Brand this charge is attributed to (per-brand cost view). Top-level field. */
  brandId?: string;
  source: 'manual' | 'system';
  meta?: Record<string, unknown>;
}): LedgerTxPlan {
  const { fromSub, fromTopup } = splitConsumption(input.amount, {
    subscriptionCredits: input.currentSub,
    topupCredits: Number.MAX_SAFE_INTEGER,
  });
  return {
    counter: {
      TableName: input.countersTable,
      Key: creditBalanceKey(input.tenantId),
      UpdateExpression:
        'SET subscriptionCredits = subscriptionCredits - :fromSub, topupCredits = topupCredits - :fromTopup',
      ConditionExpression: 'subscriptionCredits >= :fromSub AND topupCredits >= :fromTopup',
      ExpressionAttributeValues: { ':fromSub': fromSub, ':fromTopup': fromTopup },
    },
    event: buildEventItem({
      countersTable: input.countersTable,
      eventsTable: input.eventsTable,
      tenantId: input.tenantId,
      eventType: 'commit', // a charge is a consumption; reuse the commit event type
      amount: -input.amount,
      subDelta: -fromSub,
      topupDelta: -fromTopup,
      reservedDelta: 0,
      nowIso: input.nowIso,
      nowMs: input.nowMs,
      uuid: input.uuid,
      meta: {
        source: input.source,
        motif: input.motif,
        balanceAfter: input.availableBefore - input.amount,
        ...(input.brandId ? { brandId: input.brandId } : {}),
        metadata: { ...input.meta, primitive: input.primitive },
      },
    }),
  };
}

/**
 * GRANT: add credits to a bucket. `subscription` replenishment ZEROES the old
 * sub balance first (handled by the caller passing the replacement value via
 * `setSubTo`); `topup` packs add to topup. Idempotency on stripe id enforced by
 * the caller's processedInvoices conditional.
 */
export function buildGrantPlan(input: {
  countersTable: string;
  eventsTable: string;
  tenantId: string;
  bucket: 'subscription' | 'topup';
  amount: number;
  /** subscription replenish: the new absolute sub balance + expiry. */
  setSubTo?: number;
  /**
   * subscription op: 'set' (default) REPLACES the sub balance (monthly renewal,
   * old credits lost). 'add' INCREMENTS it (mid-cycle upgrade proration — keep
   * the credits already bought, add the prorated delta).
   */
  subOp?: 'set' | 'add';
  expireAt?: string;
  nowIso: string;
  nowMs: number;
  uuid: string;
  /** French ledger label, e.g. "Abonnement Agency M" / "Recharge crédits". */
  motif?: string;
  /** available BEFORE this grant; when set, stamps a balanceAfter snapshot. */
  availableBefore?: number;
  source: 'stripe' | 'admin' | 'system';
  meta?: Record<string, unknown>;
}): LedgerTxPlan {
  let counter: CounterUpdatePlan;
  let subDelta = 0;
  let topupDelta = 0;
  // Lifetime PAID total (CreditBalance.lifetimePurchased) — only Stripe-sourced
  // grants count, so admin adjustments and the agency signup trial never inflate
  // it. `ADD` creates the attribute from 0 when absent, and rides inside the
  // same transaction as the grant so the two can never drift. Appended as a
  // second clause to whichever branch's `SET` expression is built below; `:lp`
  // is a dedicated placeholder because the replenish branch has no `:amt`.
  const lifetimeClause = input.source === 'stripe' ? ' ADD lifetimePurchased :lp' : '';
  const lifetimeValue = lifetimeClause ? { ':lp': input.amount } : {};
  if (input.bucket === 'subscription' && input.subOp === 'add') {
    // Mid-cycle upgrade: INCREMENT the sub bucket by the prorated delta, keeping
    // credits already bought this period. Expiry unchanged (same billing cycle).
    counter = {
      TableName: input.countersTable,
      Key: creditBalanceKey(input.tenantId),
      UpdateExpression:
        'SET subscriptionCredits = if_not_exists(subscriptionCredits, :z) + :amt, ' +
        'topupCredits = if_not_exists(topupCredits, :z), ' +
        'reserved = if_not_exists(reserved, :z)' +
        (input.expireAt ? ', subscriptionExpireAt = :exp' : '') +
        lifetimeClause,
      ExpressionAttributeValues: {
        ':amt': input.amount,
        ':z': 0,
        ...(input.expireAt ? { ':exp': input.expireAt } : {}),
        ...lifetimeValue,
      },
    };
    subDelta = input.amount;
  } else if (input.bucket === 'subscription') {
    // Replenish: set absolute value (old sub credits are lost), set expiry.
    const setTo = input.setSubTo ?? input.amount;
    counter = {
      TableName: input.countersTable,
      Key: creditBalanceKey(input.tenantId),
      UpdateExpression:
        'SET subscriptionCredits = :v, topupCredits = if_not_exists(topupCredits, :z), ' +
        'reserved = if_not_exists(reserved, :z)' +
        (input.expireAt ? ', subscriptionExpireAt = :exp' : '') +
        lifetimeClause,
      ExpressionAttributeValues: {
        ':v': setTo,
        ':z': 0,
        ...(input.expireAt ? { ':exp': input.expireAt } : {}),
        ...lifetimeValue,
      },
    };
    subDelta = input.amount; // event records the granted amount (informational)
  } else {
    counter = {
      TableName: input.countersTable,
      Key: creditBalanceKey(input.tenantId),
      UpdateExpression:
        'SET topupCredits = if_not_exists(topupCredits, :z) + :amt, ' +
        'subscriptionCredits = if_not_exists(subscriptionCredits, :z), ' +
        'reserved = if_not_exists(reserved, :z)' +
        lifetimeClause,
      ExpressionAttributeValues: { ':amt': input.amount, ':z': 0, ...lifetimeValue },
    };
    topupDelta = input.amount;
  }
  // balanceAfter: a topup/admin grant simply adds `amount` to available. A
  // subscription replenish ZEROES old sub then sets the new value, so the delta
  // depends on the (unknown-here) old sub balance — skip the snapshot rather
  // than print a wrong number; the reconciler still has the bucket deltas.
  const balanceAfter =
    typeof input.availableBefore === 'number' && input.bucket === 'topup'
      ? input.availableBefore + input.amount
      : undefined;
  return {
    counter,
    event: buildEventItem({
      countersTable: input.countersTable,
      eventsTable: input.eventsTable,
      tenantId: input.tenantId,
      eventType: 'grant',
      amount: input.amount,
      subDelta,
      topupDelta,
      reservedDelta: 0,
      nowIso: input.nowIso,
      nowMs: input.nowMs,
      uuid: input.uuid,
      meta: { source: input.source, motif: input.motif, balanceAfter, metadata: input.meta },
    }),
  };
}

